// Styles
import styles from './BannerHeaderActionRow.module.css';

// Hooks
import useStickyTopElems from '../../../hooks/useStickyTopElems';

// Utils
import cn from '../../../utils/cn';

// Components
import {
  PopoverContent,
  PopoverTrigger,
} from '../../atoms/Popover/Popover';
import { Popover } from '../../../store/popover/PopoverContext';

/**
 * Title + action used as the detail header menu trigger.
 * Anchors the popover to the whole title block, not just the icon.
 * @param {object} props
 * @param {string} [props.title]
 * @param {React.ReactNode} [props.action]
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {() => void} props.onTriggerClick
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function BannerTitleMenu({
  title,
  action,
  open,
  onOpenChange,
  onTriggerClick,
  children,
}) {
  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      placement="bottom-end"
    >
      <PopoverTrigger asChild onClick={onTriggerClick}>
        <div className={cn([
          styles['banner-title-with-action'],
          styles['banner-title-with-action-trigger'],
        ])}
        >
          <h1 className={styles['banner-title-text']}>{title}</h1>
          {action ? (
            <div className={styles['banner-title-action']}>
              {action}
            </div>
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverContent>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * BannerHeaderActionRow component - A row with title and actions plus an optional button to collapse
 * @param {object} props
 * @param {string | React.ReactNode} [props.title]
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.rightActionButton]
 * @param {React.ReactNode} [props.titleAction]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function BannerHeaderActionRow({
  title, className = '', children, rightActionButton, titleAction,
}) {
  const { stickyHeight } = useStickyTopElems(['page-header']);
  const titleNode = typeof title === 'string' || title == null ? (
    <div className={styles['banner-title-with-action']}>
      <h1 className={styles['banner-title-text']}>{title}</h1>
      {titleAction ? (
        <div className={styles['banner-title-action']}>
          {titleAction}
        </div>
      ) : null}
    </div>
  ) : title;

  return (
    <div
      id="banner-header-action-row"
      className={cn([
        styles['banner-header-action-row'],
        className,
      ])}
      style={{ top: `${stickyHeight}px` }}
    >
      <div className={cn([
        styles['banner-title-group'],
      ])}
      >
        {children}
        {titleNode}
      </div>
      {rightActionButton || null}
    </div>
  );
}

export default BannerHeaderActionRow;
